import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "./App.scss";
import { ShipmentResponse, AddressService } from "services/AddressService";
import { NotesService, AddressNote } from "services/NotesService";
import Spinner from "helpers/Spinner";
import { error, success } from "helpers/notification";
import { Button, TextField, Typography, Card, CardContent, Box, Container, Link, Chip, Divider, ThemeProvider } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Footer from "components/Footer";
import Header from "components/Header";
import BlockedPackageNotice from "components/BlockedPackageNotice";
import theme from "./theme";

const App: React.FC = () => {
  const [trackingNumber, setTrackingNumber] = useState<string>("");
  const [trackingNumberInput, setTrackingNumberInput] = useState<string>("");
  const [recipientLastNameInput, setRecipientLastNameInput] = useState<string>("");
  const [shipmentResponse, setShipmentResponse] = useState<ShipmentResponse | null>(null);
  const [isLookingUpTrackingNumber, setIsLookingUpTrackingNumber] = useState<boolean>(false);
  const [is404Error, setIs404Error] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [existingNotes, setExistingNotes] = useState<AddressNote[]>([]);
  const [isSubmittingNotes, setIsSubmittingNotes] = useState<boolean>(false);
  const [showLastNameVerification, setShowLastNameVerification] = useState<boolean>(false);
  const [verificationLastName, setVerificationLastName] = useState<string>("");
  const [noteSubmittedSuccessfully, setNoteSubmittedSuccessfully] = useState<boolean>(false);
  const [showExistingNotes, setShowExistingNotes] = useState<boolean>(false);

  // Sephora and Best Buy packages are locked down: the recipient has to go through
  // the shipper to get their address validated, so no notes can be submitted from here.
  const isSephoraPackage = shipmentResponse?.isSephoraPackage === true;
  const isBestBuyPackage = shipmentResponse?.isBestBuyPackage === true;
  const isBlockedPackage = isSephoraPackage || isBestBuyPackage;

  const handleTrackingNumberLookup = () => {
    setIsLookingUpTrackingNumber(true);
    setIs404Error(false);

    AddressService.verifyLastName(trackingNumberInput, recipientLastNameInput)
      .then(() => {
        window.open(`/?trackingNumber=${trackingNumberInput}`, "_self");
      })
      .catch((_error: any) => {
        if (_error.response && _error.response.status === 404) {
          error(_error.response.data.message || "Tracking number or last name not found");
        } else {
          error("An error occurred while looking up the tracking number");
        }
        setIs404Error(true);
      })
      .finally(() => {
        setIsLookingUpTrackingNumber(false);
      });
  };

  const loadExistingNotes = async (addressId: number) => {
    try {
      const response = await NotesService.getAddressNotes(addressId);
      setExistingNotes(response.data.notes || []);
    } catch (err) {
      console.error("Error loading existing notes:", err);
      setExistingNotes([]);
    }
  };

  const handleSubmitNotes = () => {
    if (isBlockedPackage) {
      return;
    }

    if (!shipmentResponse || !notes.trim()) {
      error("Please enter a note before submitting");
      return;
    }
    // Show last name verification step
    setShowLastNameVerification(true);
  };

  const handleVerifyAndSubmitNotes = async () => {
    if (isBlockedPackage) {
      return;
    }

    if (!shipmentResponse || !notes.trim()) {
      error("Please enter a note before submitting");
      return;
    }

    if (!verificationLastName.trim()) {
      error("Please enter your last name to verify");
      return;
    }

    setIsSubmittingNotes(true);
    
    try {
      // Verify last name first
      await AddressService.verifyLastName(shipmentResponse.btShipmentId, verificationLastName.trim());
      
      // If verification successful, submit the note
      await NotesService.updateAddressNotes(
        shipmentResponse.address.id,
        shipmentResponse.btShipmentId,
        notes.trim()
      );
      
      success("Note added successfully!");
      setNotes("");
      setShowLastNameVerification(false);
      setVerificationLastName("");
      setNoteSubmittedSuccessfully(true);

      // Reload existing notes
      await loadExistingNotes(shipmentResponse.address.id);
    } catch (err: any) {
      console.error("Error submitting notes:", err);
      if (err.response && err.response.status === 404) {
        error("Last name verification failed. Please check your last name and try again.");
      } else {
        error("Failed to add note. Please try again.");
      }
    } finally {
      setIsSubmittingNotes(false);
    }
  };

  const handleCancelVerification = () => {
    setShowLastNameVerification(false);
    setVerificationLastName("");
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const trackingNumberParam = urlParams.get("trackingNumber");

    if (trackingNumberParam) {
      setTrackingNumber(trackingNumberParam);
      
      // Fetch shipment details
      AddressService.getParcel(trackingNumberParam)
        .then((response: any) => {
          setShipmentResponse(response.data);
          // Load existing notes for this address
          loadExistingNotes(response.data.address.id);
        })
        .catch((_error: any) => {
          if (_error.response && _error.response.status === 404) {
            setIs404Error(true);
            error("Parcel not found");
          } else {
            error("An error occurred while loading parcel information");
          }
        });
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <div className="App">
          <Header />
          <Routes>
          <Route
            path="/"
            element={
              <>
                {!trackingNumber && (
                  <Container 
                    maxWidth="sm" 
                    sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 'calc(100vh - 120px)' // Adjusting for header and footer
                    }}
                  >
                    <Card sx={{ width: '100%' }}>
                      <CardContent>
                        <p style={{ marginBottom: '0' }}>
                          <b>Enter the information below to add notes for your package:</b>
                        </p>
                        
                        <Box component="form" noValidate autoComplete="off">
                          <TextField
                            fullWidth
                            id="trackingNumberInput"
                            label="Tracking ID"
                            value={trackingNumberInput}
                            onChange={(e) => setTrackingNumberInput(e.target.value)}
                            margin="normal"
                            variant="outlined"
                          />
                          <TextField
                            fullWidth
                            id="recipientLastNameInput"
                            label="Recipient's Last Name"
                            value={recipientLastNameInput}
                            onChange={(e) => setRecipientLastNameInput(e.target.value)}
                            margin="normal"
                            variant="outlined"
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={isLookingUpTrackingNumber}
                            onClick={handleTrackingNumberLookup}
                            sx={{ mt: 2 }}
                          >
                            {isLookingUpTrackingNumber ? <Spinner /> : "Continue"}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Container>
                )}

                {trackingNumber && shipmentResponse && !is404Error && (
                  <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                    {isSephoraPackage && <BlockedPackageNotice shipperName="Sephora" />}
                    {isBestBuyPackage && <BlockedPackageNotice shipperName="Best Buy" />}

                    <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>

                      {/* Left Side - Delivery Address Card */}
                      <Box sx={{
                        flex: { md: '0 0 350px' },
                        order: { xs: 2, md: 1 }
                      }}>
                        <Card sx={{ height: 'fit-content', mb: 2 }}>
                          <CardContent sx={{ p: 3 }}>
                            <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.75rem', letterSpacing: 1 }}>
                              DELIVERY ADDRESS
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 1, fontWeight: 500 }}>
                              {shipmentResponse.address.company}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {shipmentResponse.address.street1}
                            </Typography>
                            {shipmentResponse.address.street2 && (
                              <Typography variant="body2">
                                {shipmentResponse.address.street2}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              {shipmentResponse.address.city}, {shipmentResponse.address.state}
                            </Typography>
                            <Typography variant="body2">
                              {shipmentResponse.address.zip}
                            </Typography>

                            <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.75rem', letterSpacing: 1, mt: 3, display: 'block' }}>
                              TRACKING ID
                            </Typography>
                            <Link
                              href={`${process.env.REACT_APP_TRACKING_URL}Tracking?ShipmentId=${shipmentResponse.btShipmentId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                fontWeight: "medium",
                                color: 'primary.main',
                                mt: 0.5,
                                display: 'block'
                              }}
                            >
                              {shipmentResponse.trackingId}
                            </Link>
                          </CardContent>
                        </Card>

                        {/* Previous Notes Toggle and Display */}
                        {existingNotes.length > 0 && (
                          <>
                            <Button
                              variant="outlined"
                              fullWidth
                              onClick={() => setShowExistingNotes(!showExistingNotes)}
                              sx={{
                                mb: 2,
                                textTransform: 'none',
                                justifyContent: 'space-between',
                                color: 'text.primary',
                                borderColor: 'divider'
                              }}
                            >
                              Show existing notes
                              <Typography component="span" sx={{ ml: 1 }}>
                                {showExistingNotes ? '−' : '+'}
                              </Typography>
                            </Button>

                            {showExistingNotes && (
                              <Card>
                                <CardContent sx={{ p: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
                                    Previous Notes
                                  </Typography>
                                  {existingNotes.map((note, index) => {
                                    const isLatest = index === 0;
                                    return (
                                      <Box key={note.id || index}>
                                        <Box sx={{ py: 1, px: 0 }}>
                                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem' }}>
                                              {note.note}
                                            </Typography>
                                            {isLatest && (
                                              <Chip
                                                label="Latest"
                                                size="small"
                                                color="primary"
                                                sx={{
                                                  height: 18,
                                                  fontSize: "0.625rem",
                                                  fontWeight: 500
                                                }}
                                              />
                                            )}
                                          </Box>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            {new Date(note.objectCreated).toLocaleDateString("en-US")}
                                          </Typography>
                                        </Box>
                                        {index < existingNotes.length - 1 && <Divider sx={{ my: 0.5 }} />}
                                      </Box>
                                    );
                                  })}
                                </CardContent>
                              </Card>
                            )}
                          </>
                        )}
                      </Box>

                      {/* Right Side - Notes Section */}
                      <Box sx={{
                        flex: 1,
                        order: { xs: 1, md: 2 }
                      }}>

                        {/* Add New Note or Success Screen */}
                        <Card>
                          <CardContent sx={{ p: 4 }}>
                            {!noteSubmittedSuccessfully ? (
                              <>
                                <Typography variant="h5" gutterBottom>
                                  Anything your driver should know?
                                </Typography>
                                <Typography variant="body1" gutterBottom sx={{ mb: 3, color: 'text.secondary' }}>
                                  Please leave delivery instructions for your driver here (e.g. gate/access code or specific delivery location).
                                </Typography>

                                <TextField
                                  fullWidth
                                  multiline
                                  rows={4}
                                  variant="outlined"
                                  placeholder="Please access the building using the access code 5678#"
                                  value={notes}
                                  onChange={(e) => setNotes(e.target.value)}
                                  disabled={isBlockedPackage}
                                  sx={{
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: '#f8f9fa'
                                    }
                                  }}
                                />

                                {!showLastNameVerification ? (
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={isBlockedPackage || isSubmittingNotes || !notes.trim()}
                                    onClick={handleSubmitNotes}
                                    sx={{
                                      bgcolor: notes.trim() ? "#1976d2" : "#6c757d",
                                      "&:hover": {
                                        bgcolor: notes.trim() ? "#1565c0" : "#5a6268"
                                      },
                                      py: 1.5,
                                      textTransform: 'none',
                                      fontSize: '1rem'
                                    }}
                                  >
                                    Send notes
                                  </Button>
                                ) : (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 2, fontWeight: "medium" }}>
                                      Please verify your last name to submit the note:
                                    </Typography>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      label="Your Last Name"
                                      value={verificationLastName}
                                      onChange={(e) => setVerificationLastName(e.target.value)}
                                      sx={{ mb: 2 }}
                                      disabled={isBlockedPackage || isSubmittingNotes}
                                    />
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Button
                                        variant="outlined"
                                        onClick={handleCancelVerification}
                                        disabled={isBlockedPackage || isSubmittingNotes}
                                        sx={{ flex: 1 }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        variant="contained"
                                        onClick={handleVerifyAndSubmitNotes}
                                        disabled={isBlockedPackage || isSubmittingNotes || !verificationLastName.trim()}
                                        sx={{
                                          flex: 1,
                                          bgcolor: "#6c757d",
                                          "&:hover": {
                                            bgcolor: "#5a6268"
                                          }
                                        }}
                                      >
                                        {isSubmittingNotes ? <Spinner /> : "Verify & Send"}
                                      </Button>
                                    </Box>
                                  </Box>
                                )}
                              </>
                            ) : (
                              /* Success Screen */
                              <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                py: 6
                              }}>
                                <CheckCircleIcon
                                  sx={{
                                    fontSize: 80,
                                    color: '#1976d2',
                                    mb: 3
                                  }}
                                />
                                <Typography variant="h4" gutterBottom sx={{ fontWeight: 500 }}>
                                  Thank you!
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 400 }}>
                                  Your delivery notes were sent to the driver, who will review them before completing your delivery.
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>

                      </Box>
                    </Box>
                  </Container>
                )}
                

                {is404Error && (
                  <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="error">
                          Parcel Not Found
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          Please check your tracking number and recipient last name.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Container>
                )}
              </>
            }
          />
          </Routes>
          <Footer />
        </div>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;